//! Formula editing helpers for the in-cell editor (point-and-click refs, F4, etc.).
//! Grid highlights are driven by cellEditorFormula; in-editor coloring uses CellEditorView.

import {
  closeParentheses,
  isFormulaValid,
  toggleReferenceAtCaret,
  wantsCellRefAtCaret,
} from '@/app/cellEditor/cellEditorFormula';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { inlineEditorHandler } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorHandler';
import { inlineEditorInput } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorInput';

class InlineEditorFormula {
  private insertingCells?: { value: string; position: number };

  constructor() {
    events.on('cursorPosition', this.cursorMoved);
  }

  cellHighlights(location: { sheetId: string; x: number; y: number }, formula: string) {
    // Grid cell-reference highlights are updated via updateFormulaHighlights in InlineEditorBody.
    void location;
    void formula;
  }

  clearDecorations() {
    // Formula token coloring is handled by CellEditorView.
  }

  removeInsertingCells() {
    if (!this.insertingCells) return;
    const { value, position } = this.insertingCells;
    inlineEditorInput.deleteText(position, value.length);
    this.insertingCells = undefined;
  }

  private insertInsertingCells(a1Notation: string) {
    const position = this.insertingCells?.position ?? inlineEditorInput.getCaret();
    const oldLength = this.insertingCells?.value.length ?? 0;
    inlineEditorInput.replaceRange(a1Notation, position, position + oldLength, a1Notation.length);
    this.insertingCells = { value: a1Notation, position };
  }

  addInsertingCells(position: number) {
    this.insertingCells = { value: '', position };
  }

  endInsertingCells() {
    this.insertingCells = undefined;
  }

  cursorMoved = () => {
    if (inlineEditorHandler.cursorIsMoving) {
      const cursor = sheets.sheet.cursor;
      const location = inlineEditorHandler.location;
      if (!location) {
        throw new Error('Expected inlineEditorHandler.location to be defined in cursorMoved');
      }

      inlineEditorHandler.cursorIsMoving = true;
      inlineEditorInput.removeSelection();
      this.insertInsertingCells(cursor.toA1String(location.sheetId));
      inlineEditorHandler.sendMultiplayerUpdate();
      setTimeout(inlineEditorInput.focus, 0);
    }
  };

  wantsCellRef() {
    return wantsCellRefAtCaret(inlineEditorInput.get(), inlineEditorInput.getCaret());
  }

  isFormulaValid(testFormula?: string, skipCloseParenthesisCheck?: true): boolean {
    if (inlineEditorHandler.cursorIsMoving) return false;
    if (inlineEditorInput.hasSelection()) return false;

    const location = inlineEditorHandler.location;
    if (!location) return false;
    const formula = (testFormula ?? inlineEditorInput.get()).slice(1);
    if (!isFormulaValid('=' + formula, location)) {
      if (skipCloseParenthesisCheck) return false;
      const value = this.closeParentheses();
      if (value && value !== testFormula) {
        return isFormulaValid(value, location);
      }
      return false;
    }
    return true;
  }

  closeParentheses(): string | undefined {
    const formula = inlineEditorInput.get();
    const balanced = closeParentheses(formula);
    if (balanced !== formula && this.isFormulaValid(balanced, true)) {
      inlineEditorInput.set(balanced);
      return balanced;
    }
    return formula;
  }

  toggleReference() {
    const value = inlineEditorInput.get();
    const caret = inlineEditorInput.getCaret();
    const result = toggleReferenceAtCaret(value, caret);
    if (!result) return;
    inlineEditorInput.set(result.value, result.caret);
  }
}

export const inlineEditorFormula = new InlineEditorFormula();
